import { useCallback, useEffect, useRef, useState } from "react";

/**
 * MediaRecorder-based voice recorder with a live level meter.
 *
 * state: idle | requesting | recording | paused | stopped | error
 * Picks the best container the browser supports (webm/opus on Chrome/Android, mp4/aac on iOS Safari).
 * Auto-stops at maxSeconds (default 10 min) to stay well below whisper's 25 MB limit.
 */
const CANDIDATES = [
  "audio/webm;codecs=opus",
  "audio/webm",
  "audio/mp4;codecs=mp4a.40.2",
  "audio/mp4",
  "audio/ogg;codecs=opus",
];

export const pickMimeType = () => {
  if (typeof MediaRecorder === "undefined") return "";
  for (const c of CANDIDATES) {
    try {
      if (MediaRecorder.isTypeSupported(c)) return c;
    } catch (e) {
      /* ignore */
    }
  }
  return "";
};

export const extensionFor = (mime) => {
  const base = (mime || "").split(";")[0];
  if (base.includes("mp4")) return "m4a";
  if (base.includes("ogg")) return "ogg";
  if (base.includes("mpeg")) return "mp3";
  if (base.includes("wav")) return "wav";
  return "webm";
};

export const formatClock = (s) => {
  const m = Math.floor(s / 60);
  const r = Math.floor(s % 60);
  return `${m}:${String(r).padStart(2, "0")}`;
};

export const useAudioRecorder = ({ maxSeconds = 600, levelBars = 28 } = {}) => {
  const supported =
    typeof navigator !== "undefined" &&
    !!navigator.mediaDevices?.getUserMedia &&
    typeof MediaRecorder !== "undefined";

  const [state, setState] = useState("idle");
  const [seconds, setSeconds] = useState(0);
  const [level, setLevel] = useState(0); // 0..1 current input level
  const [levels, setLevels] = useState(() => Array(levelBars).fill(0)); // rolling history for a waveform
  const [blob, setBlob] = useState(null);
  const [mimeType, setMimeType] = useState("");
  const [error, setError] = useState("");

  const recRef = useRef(null);
  const streamRef = useRef(null);
  const chunksRef = useRef([]);
  const audioCtxRef = useRef(null);
  const rafRef = useRef(0);
  const tickRef = useRef(0);
  const startedAtRef = useRef(0);
  const accumulatedRef = useRef(0); // seconds recorded before the current segment (pause support)

  const cleanupAnalyser = () => {
    if (rafRef.current) cancelAnimationFrame(rafRef.current);
    rafRef.current = 0;
    try {
      audioCtxRef.current?.close();
    } catch (e) {
      /* noop */
    }
    audioCtxRef.current = null;
  };

  const stopTimer = () => {
    if (tickRef.current) clearInterval(tickRef.current);
    tickRef.current = 0;
  };

  const releaseStream = () => {
    streamRef.current?.getTracks().forEach((t) => t.stop());
    streamRef.current = null;
  };

  const stop = useCallback(() => {
    const rec = recRef.current;
    if (!rec || rec.state === "inactive") return;
    try {
      rec.stop();
    } catch (e) {
      /* noop */
    }
  }, []);

  const startTimer = () => {
    startedAtRef.current = Date.now();
    stopTimer();
    tickRef.current = setInterval(() => {
      const s = accumulatedRef.current + (Date.now() - startedAtRef.current) / 1000;
      setSeconds(s);
      if (s >= maxSeconds) stop();
    }, 200);
  };

  const startAnalyser = (stream) => {
    try {
      const Ctx = window.AudioContext || window.webkitAudioContext;
      if (!Ctx) return;
      const ctx = new Ctx();
      const src = ctx.createMediaStreamSource(stream);
      const analyser = ctx.createAnalyser();
      analyser.fftSize = 512;
      analyser.smoothingTimeConstant = 0.6;
      src.connect(analyser);
      audioCtxRef.current = ctx;
      const data = new Uint8Array(analyser.frequencyBinCount);
      let last = 0;
      const loop = (t) => {
        analyser.getByteTimeDomainData(data);
        let sum = 0;
        for (let i = 0; i < data.length; i++) {
          const v = (data[i] - 128) / 128;
          sum += v * v;
        }
        const rms = Math.sqrt(sum / data.length);
        const lv = Math.min(1, rms * 3.2);
        setLevel(lv);
        if (t - last > 90) {
          last = t;
          setLevels((prev) => [...prev.slice(1), lv]);
        }
        rafRef.current = requestAnimationFrame(loop);
      };
      rafRef.current = requestAnimationFrame(loop);
    } catch (e) {
      /* level meter is cosmetic */
    }
  };

  const start = useCallback(async () => {
    if (!supported) {
      setError("unsupported");
      setState("error");
      return;
    }
    setError("");
    setBlob(null);
    setSeconds(0);
    accumulatedRef.current = 0;
    setLevels(Array(levelBars).fill(0));
    setState("requesting");
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      });
      streamRef.current = stream;
      const mime = pickMimeType();
      const rec = mime ? new MediaRecorder(stream, { mimeType: mime, audioBitsPerSecond: 48000 }) : new MediaRecorder(stream);
      chunksRef.current = [];
      rec.ondataavailable = (e) => {
        if (e.data && e.data.size > 0) chunksRef.current.push(e.data);
      };
      rec.onstop = () => {
        const type = rec.mimeType || mime || "audio/webm";
        const b = new Blob(chunksRef.current, { type });
        setBlob(b);
        setMimeType(type);
        stopTimer();
        cleanupAnalyser();
        releaseStream();
        setLevel(0);
        setState("stopped");
      };
      rec.onerror = () => {
        setError("recorder");
        setState("error");
        stopTimer();
        cleanupAnalyser();
        releaseStream();
      };
      recRef.current = rec;
      setMimeType(rec.mimeType || mime);
      rec.start(1000); // timeslice -> chunks every second (robust against tab suspension)
      startAnalyser(stream);
      startTimer();
      setState("recording");
    } catch (e) {
      const name = e?.name || "";
      setError(name === "NotAllowedError" || name === "SecurityError" ? "not-allowed" : name === "NotFoundError" ? "no-mic" : "failed");
      setState("error");
      releaseStream();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [supported, levelBars]);

  const pause = useCallback(() => {
    const rec = recRef.current;
    if (!rec || rec.state !== "recording") return;
    try {
      rec.pause();
      accumulatedRef.current += (Date.now() - startedAtRef.current) / 1000;
      stopTimer();
      setState("paused");
    } catch (e) {
      /* noop */
    }
  }, []);

  const resume = useCallback(() => {
    const rec = recRef.current;
    if (!rec || rec.state !== "paused") return;
    try {
      rec.resume();
      startTimer();
      setState("recording");
    } catch (e) {
      /* noop */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const reset = useCallback(() => {
    stop();
    stopTimer();
    cleanupAnalyser();
    releaseStream();
    recRef.current = null;
    chunksRef.current = [];
    accumulatedRef.current = 0;
    setBlob(null);
    setSeconds(0);
    setLevel(0);
    setLevels(Array(levelBars).fill(0));
    setError("");
    setState("idle");
  }, [stop, levelBars]);

  // Release the microphone if the component unmounts mid-recording
  useEffect(() => {
    return () => {
      try {
        recRef.current?.state !== "inactive" && recRef.current?.stop();
      } catch (e) {
        /* noop */
      }
      stopTimer();
      cleanupAnalyser();
      releaseStream();
    };
  }, []);

  return { supported, state, seconds, level, levels, blob, mimeType, error, start, pause, resume, stop, reset, maxSeconds };
};
